import React, { useMemo } from 'react';
import { Icon, ContentBlock } from '../../DesignSystem';
import './ImpactStatsCard.css';

interface ImpactStatsCardProps {
  stats: {
    touristsReached: number;
    eventsCreated: number;
  };
  loading?: boolean;
}

// Generic storytelling variants (always available)
const GENERIC_VARIANTS = [
  "Since you joined W2D — imagine all the conversations that started!",
  "Every view is someone discovering what you create!",
  "That's a lot of new faces discovering your events!",
  "Your events are making waves in the community!",
  "Every tourist who sees your event is a potential guest walking through your door!",
];

/**
 * Card displaying cumulative impact stats
 * Shows tourists reached + events created + storytelling phrase
 */
const ImpactStatsCard: React.FC<ImpactStatsCardProps> = ({
  stats,
  loading = false
}) => {
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  // Select a random storytelling variant at mount
  const storytellingPhrase = useMemo(() => {
    const canUseNumbers = stats.eventsCreated >= 3 && stats.touristsReached >= 30;
    const flyersSaved = Math.ceil(stats.touristsReached * 0.75);

    const pool: string[] = [...GENERIC_VARIANTS];

    if (canUseNumbers) {
      pool.push(
        `${stats.eventsCreated} events, ${stats.touristsReached} views — that's roughly ${flyersSaved} fewer flyers to print!`
      );
      pool.push(
        `Your events were seen ${stats.touristsReached} times — and you didn't print a single flyer for that!`
      );
    }

    return pool[Math.floor(Math.random() * pool.length)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Pick once at mount

  if (loading) {
    return (
      <ContentBlock className="impact-stats-card impact-stats-card--loading" border="light">
        <div className="impact-stats-card__skeleton">
          <div className="impact-stats-card__skeleton-stats">
            <div className="impact-stats-card__skeleton-stat" />
            <div className="impact-stats-card__skeleton-stat" />
          </div>
        </div>
      </ContentBlock>
    );
  }

  const statItems = [
    {
      icon: 'Eye' as const,
      value: stats.touristsReached,
      label: 'Event views'
    },
    {
      icon: 'Calendar' as const,
      value: stats.eventsCreated,
      label: 'Events created'
    }
  ];

  return (
    <ContentBlock className="impact-stats-card" border="light">
      <div className="impact-stats-card__header">
        <Icon name="TrendingUp" size={18} />
        <span>My Impact</span>
      </div>

      <div className="impact-stats-card__content">
        <div className="impact-stats-card__stats">
          {statItems.map((item) => (
            <div key={item.label} className="impact-stats-card__stat">
              <div className="impact-stats-card__stat-icon">
                <Icon name={item.icon} size={20} />
              </div>
              <div className="impact-stats-card__stat-value">
                {formatNumber(item.value)}
              </div>
              <div className="impact-stats-card__stat-label">
                {item.label}
              </div>
            </div>
          ))}
        </div>

        <div className="impact-stats-card__storytelling">
          {storytellingPhrase}
        </div>

        <div className="impact-stats-card__footer">
          <Icon name="Clock" size={12} />
          <span>Since your registration</span>
        </div>
      </div>
    </ContentBlock>
  );
};

export default ImpactStatsCard;
